/** QuickPlan — Rendu d'une instance et de ce qu'elle porte.
 *
 *  <group pos/rot>            ← placement propre (repère du parent) — c'est lui que le gizmo déplace
 *    <group matrix=J(open)>   ← articulation : la pièce ET ses enfants tournent / glissent
 *      primitives, fixations, instances enfants…
 */
import { memo, useMemo, useState } from 'react';
import * as THREE from 'three';
import { geometryFor, edgesFor, materialFor, fixingGeometry, fixingMaterial, EDGE_MAT, EDGE_MAT_SEL } from './cache.js';
import { resolved } from '../geom.js';
import { jointFrame } from '../../shared/frames.js';

const DEG = Math.PI / 180;
const Z = new THREE.Vector3(0, 0, 1);

/** Registres id → Object3D : placement (gizmo) et repère porteur (pose de fixations). */
export const objectRegistry = new Map();
export const frameRegistry = new Map();

function jointMatrix(joint, open) {
  const f = jointFrame(joint, open);
  const m = new THREE.Matrix4();
  m.set(
    f.R[0][0], f.R[0][1], f.R[0][2], f.t[0],
    f.R[1][0], f.R[1][1], f.R[1][2], f.t[1],
    f.R[2][0], f.R[2][1], f.R[2][2], f.t[2],
    0, 0, 0, 1,
  );
  return m;
}

function Fixings({ list }) {
  return list.filter((f) => f.type !== 'percage').map((f, k) => {
    const q = new THREE.Quaternion().setFromUnitVectors(Z, new THREE.Vector3(...f.dir));
    return (
      <mesh key={k} position={f.pos} quaternion={q} geometry={fixingGeometry(f)} material={fixingMaterial(f.type)} raycast={() => null} renderOrder={2} />
    );
  });
}

function InstanceNode({ inst, kids, ctx }) {
  const { lookup, selSet, hidden, showEdges, showFixings, onPick, onDouble, onFix, tool } = ctx;
  const [hover, setHover] = useState(false);
  const comp = lookup(inst.componentId);
  const res = comp ? resolved(comp, inst.params) : null;
  const jm = useMemo(() => (res?.joint ? jointMatrix(res.joint, inst.open) : null), [res, inst.open]);
  if (!comp || !res || res.error || hidden.has(inst.id)) return null;
  const selected = selSet.has(inst.id);
  const state = selected ? 'selected' : hover ? 'hover' : '';

  const content = (
    <>
      {res.parts.map((p, k) => (
        <group key={k} position={p.pos} rotation={p.rot ? [p.rot[0] * DEG, p.rot[1] * DEG, p.rot[2] * DEG] : undefined}>
          <mesh geometry={geometryFor(p)} material={materialFor(inst.color ?? p.color ?? comp.color, comp.category, state, p.opacity)} />
          {showEdges && <lineSegments geometry={edgesFor(p)} material={selected ? EDGE_MAT_SEL : EDGE_MAT} raycast={() => null} />}
        </group>
      ))}
      {showFixings && res.fixings.length > 0 && <Fixings list={res.fixings} />}
      {kids.get(inst.id)?.map((c) => <InstanceNode key={c.id} inst={c} kids={kids} ctx={ctx} />)}
    </>
  );

  return (
    <group
      ref={(o) => { if (o) objectRegistry.set(inst.id, o); else objectRegistry.delete(inst.id); }}
      position={inst.pos}
      rotation={[inst.rot[0] * DEG, inst.rot[1] * DEG, inst.rot[2] * DEG]}
      onClick={(e) => {
        if (e.delta > 4) return;
        e.stopPropagation();
        if (tool === 'fixation') {
          const n = e.face ? e.face.normal.clone().transformDirection(e.object.matrixWorld) : new THREE.Vector3(0, 0, 1);
          onFix(inst.id, e.point.clone(), n);
          return;
        }
        onPick(inst.id, e.nativeEvent.ctrlKey || e.nativeEvent.shiftKey || e.nativeEvent.metaKey);
      }}
      onDoubleClick={(e) => { e.stopPropagation(); onDouble(inst.id); }}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = tool === 'fixation' ? 'crosshair' : 'pointer'; }}
      onPointerOut={() => { setHover(false); document.body.style.cursor = ''; }}
    >
      {jm ? (
        <group matrixAutoUpdate={false} matrix={jm} ref={(o) => { if (o) frameRegistry.set(inst.id, o); }}>
          {content}
        </group>
      ) : (
        <group ref={(o) => { if (o) frameRegistry.set(inst.id, o); }}>{content}</group>
      )}
    </group>
  );
}

export default memo(InstanceNode);
